import { Injectable } from '@angular/core';
import { GroupApiModel } from '@app/api/api-models';
import { AclItemApiModel } from '@app/api/api-models/AclItemApiModel';
import { ProductCodeToNamePipe } from '@shared/pipes/product-code-to-name.pipe';

@Injectable()
export class AclListBuilderService {
  public presets = Object.freeze({
    AnyTrainee: new AclItemApiModel('Any Trainee', { Type: 0, ProgramRole: 0 }),
    TheTrainee: new AclItemApiModel('The Trainee', { Type: 0, ProgramRole: 0 }),
    AnyEmployee: new AclItemApiModel('Any Employee', { Type: 0, ProgramRole: 0 }),
    TheEmployee: new AclItemApiModel('The Employee', { Type: 0, ProgramRole: 0 }),
    AnyTrainer: new AclItemApiModel('Any Trainer', { Type: 0, ProgramRole: 1 }),
    AnySupervisor: new AclItemApiModel('Any Supervisor', { Type: 0, ProgramRole: 2 }),
    AnyCoordinator: new AclItemApiModel('Any Coordinator', { Type: 0, ProgramRole: 3 }),
    AnySiteAdministrator: new AclItemApiModel('Any Site Administrator', { Type: 0, SiteRole: 1 }),
    AnyObserver: new AclItemApiModel('Any Observer', { Type: 0, SiteRole: 3 }),
    RelatedTrainee: new AclItemApiModel('Related Trainee', { Type: 0, ProgramRole: 0, TrackRelatedToSubject: true }),
    RelatedEmployee: new AclItemApiModel('Related Employee', { Type: 0, ProgramRole: 0, TrackRelatedToSubject: true }),
    RelatedTrainer: new AclItemApiModel('Related Trainer', { Type: 0, ProgramRole: 1, TrackRelatedToSubject: true }),
    RelatedSupervisor: new AclItemApiModel('Related Supervisor', {
      Type: 0,
      ProgramRole: 2,
      TrackRelatedToSubject: true
    }),
    RelatedCoordinator: new AclItemApiModel('Related Coordinator', {
      Type: 0,
      ProgramRole: 3,
      TrackRelatedToSubject: true
    }),
    RelatedSiteAdministrator: new AclItemApiModel('Related Site Administrator', {
      Type: 0,
      SiteRole: 1,
      TrackRelatedToSubject: true
    }),
    FormCreator: new AclItemApiModel('Form Creator', { Type: 1 }),
    FormSubject: new AclItemApiModel('Form Subject', { Type: 2 }),
    QuizCreator: new AclItemApiModel('Quiz Creator', { Type: 1 }),
    QuizSubject: new AclItemApiModel('Quiz Subject', { Type: 2 }),
    Self: new AclItemApiModel('Self', { Type: 1 }),
    Program: new AclItemApiModel('Program', { Type: 3 })
  });

  private productCodeToNamePipe = new ProductCodeToNamePipe();

  public getDefaultSubjectAcl(): AclItemApiModel[] {
    return [this.presets.AnyTrainee];
  }

  public getCreatedStepAcl(): AclItemApiModel[] {
    return [this.presets.FormCreator];
  }

  public buildPresets(aclType: string, groups: GroupApiModel[] = null): AclItemApiModel[] {
    switch (aclType) {
      case 'source':
        return this.getSourcePresets();
      case 'target':
        return this.getTargetPresets();
      case 'groups':
        return this.getGroupPresets(groups);
      case 'quizTarget':
        return this.getQuizTargetPresets();
      case 'quizGeneral':
        return this.getQuizGeneralPresets();
      case 'peFormTarget':
        return this.getPeFormTargetPresets();
      case 'peFormSource':
        return this.getPeFormSourcePresets();
      case 'peFormGeneral':
        return this.getPeFormGeneralPresets();
      case 'alert':
        return this.getAlertPresets();
      case 'peAlert':
        return this.getPeAlertPresets();
      case 'editorReview': {
        const presets = this.getGeneralPresets();
        return presets.filter((x) => x.Type !== 0 || x.SiteRole !== 3); // remove observer
      }
      default:
        return this.getGeneralPresets();
    }
  }

  private getGroupPresets(groups: GroupApiModel[]): AclItemApiModel[] {
    if (groups) {
      return groups.map(
        (g: GroupApiModel) =>
          new AclItemApiModel(g.Name, {
            Type: 0,
            GroupId: g.Id,
            Caption: g.UsersCount.toString() + (g.UsersCount == 1 ? ' member' : ' members'),
            Originator: this.productCodeToNamePipe.transform(g.Originator)
          })
      );
    }

    return new Array<AclItemApiModel>();
  }

  private getSourcePresets(): AclItemApiModel[] {
    return [
      this.presets.AnyTrainee,
      this.presets.AnyTrainer,
      this.presets.AnySupervisor,
      this.presets.AnyCoordinator,
      this.presets.AnySiteAdministrator
    ];
  }

  private getTargetPresets(): AclItemApiModel[] {
    return [
      this.presets.AnyTrainee,
      this.presets.AnyTrainer,
      this.presets.AnySupervisor,
      this.presets.AnyCoordinator,
      this.presets.AnySiteAdministrator,
      this.presets.RelatedTrainee,
      this.presets.RelatedTrainer,
      this.presets.RelatedSupervisor,
      this.presets.RelatedCoordinator,
      this.presets.RelatedSiteAdministrator,
      this.presets.Program,
      this.presets.Self
    ];
  }

  private getGeneralPresets(): AclItemApiModel[] {
    return [
      this.presets.AnyTrainee,
      this.presets.AnyTrainer,
      this.presets.AnySupervisor,
      this.presets.AnyCoordinator,
      this.presets.AnySiteAdministrator,
      this.presets.AnyObserver,
      this.presets.FormCreator,
      this.presets.FormSubject
    ];
  }

  private getQuizTargetPresets(): AclItemApiModel[] {
    return [this.presets.AnyTrainee, this.presets.RelatedTrainee];
  }

  private getQuizGeneralPresets(): AclItemApiModel[] {
    return [
      this.presets.AnyTrainee,
      this.presets.AnyTrainer,
      this.presets.AnySupervisor,
      this.presets.AnyCoordinator,
      this.presets.AnySiteAdministrator,
      this.presets.QuizCreator,
      this.presets.QuizSubject
    ];
  }

  private getPeFormTargetPresets(): AclItemApiModel[] {
    return [
      this.presets.AnyEmployee,
      this.presets.AnySupervisor,
      this.presets.AnyCoordinator,
      this.presets.AnySiteAdministrator,
      this.presets.RelatedEmployee,
      this.presets.RelatedSupervisor,
      this.presets.RelatedCoordinator,
      this.presets.RelatedSiteAdministrator,
      this.presets.Self
    ];
  }

  private getPeFormSourcePresets(): AclItemApiModel[] {
    return [
      this.presets.AnyEmployee,
      this.presets.AnySupervisor,
      this.presets.AnyCoordinator,
      this.presets.AnySiteAdministrator
    ];
  }

  private getPeFormGeneralPresets(): AclItemApiModel[] {
    return [
      this.presets.AnyEmployee,
      this.presets.AnySupervisor,
      this.presets.AnyCoordinator,
      this.presets.AnySiteAdministrator,
      this.presets.AnyObserver,
      this.presets.FormCreator,
      this.presets.FormSubject
    ];
  }

  private getAlertPresets(): AclItemApiModel[] {
    return [
      this.presets.AnyTrainer,
      this.presets.RelatedTrainer,
      this.presets.AnySupervisor,
      this.presets.AnyCoordinator,
      this.presets.AnySiteAdministrator,
      this.presets.TheTrainee
    ];
  }

  private getPeAlertPresets(): AclItemApiModel[] {
    return [
      this.presets.AnySupervisor,
      this.presets.AnyCoordinator,
      this.presets.AnySiteAdministrator,
      this.presets.TheEmployee
    ];
  }
}
